import { getClient } from "src/api/api.ts";
import { HttpError } from "src/api/httpError.ts";
import { PageRequest } from "src/model/pageRequest.ts";
import { PaginatedResponse } from "src/model/paginatedResponse.ts";
import { User, UserPreferences, UserWithPreferences } from "src/model/user.ts";
import { UserSaveRequest } from "src/model/userSaveRequest.ts";

export interface Auth {
  accessToken: string;
  refreshToken: string;
}

export const login = async (username: string, password: string): Promise<Auth> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/Login", {
    body: {
      username: username,
      password: password,
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to login");
};

export const refreshToken = async (refreshToken: string): Promise<Auth> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/RefreshAccessToken", {
    body: {
      refreshToken: refreshToken,
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to fetch access token");
};

export const requestPasswordReset = async (mail: string): Promise<void> => {
  const {
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/Users/PasswordResetRequest", {
    body: {
      mail: mail,
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }
};

export const changePassword = async (token: string, password: string): Promise<void> => {
  const {
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/Users/PasswordReset", {
    body: {
      passwordChangeToken: token,
      password: password,
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }
};

export const fetchUsers = async (pagination: PageRequest): Promise<PaginatedResponse<User>> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().GET("/API/iSEA5/V2.1/Users", {
    params: {
      query: {
        limit: pagination.limit,
        offset: pagination.offset,
      },
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to fetch users");
};

export const fetchUserDetails = async (userId: number): Promise<User> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().GET("/API/iSEA5/V2.1/Users/{userId}", {
    params: {
      path: {
        userId: userId,
      },
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return data;
  }

  throw new Error("Failed to fetch user details");
};

export const createUser = async (userSaveRequest: UserSaveRequest): Promise<void> => {
  const {
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/Users", {
    body: userSaveRequest,
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }
};

export const updateUser = async (userId: number, userSaveRequest: UserSaveRequest): Promise<void> => {
  const {
    error,
    response: { status },
  } = await getClient().PUT("/API/iSEA5/V2.1/Users/{userId}", {
    params: {
      path: {
        userId: userId,
      },
    },
    body: userSaveRequest,
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }
};

export const deleteUser = async (userId: number): Promise<void> => {
  const {
    error,
    response: { status },
  } = await getClient().DELETE("/API/iSEA5/V2.1/Users/{userId}", {
    params: {
      path: {
        userId: userId,
      },
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }
};

export const fetchOwnUserDetails = async (): Promise<UserWithPreferences> => {
  const {
    data,
    error,
    response: { status },
  } = await getClient().GET("/API/iSEA5/V2.1/Users/Me");

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }

  if (data !== undefined) {
    return { ...data, preferences: data.preferences !== null ? JSON.parse(data.preferences) : null };
  }

  throw new Error("Failed to fetch user preferences");
};

export const updatePreferences = async (preferences: UserPreferences): Promise<void> => {
  const {
    error,
    response: { status },
  } = await getClient().POST("/API/iSEA5/V2.1/Users/Me/Preferences", {
    body: {
      preferences: JSON.stringify(preferences),
    },
  });

  if (error !== undefined) {
    if (error.loggingMessage) {
      throw new HttpError(status, error.loggingMessage);
    }
    throw new HttpError(status, error.message);
  }
};
